import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { Episode, Movie, MovieDetails } from 'types/Asset';
import { PurchaseOptionConfirmation } from 'components/PurchaseOptionConfirmation/PurchaseOptionConfirmation';
import { Offer, RentData, Service, SubscribeOption } from 'types/Entitlement';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import GSApi from 'api/Api';
import { setPurchaseSynced } from 'providers/reducers/purchase-synced-reducer';
import { AlertDialog } from 'components/AlertDialog/AlertDialog';
import translate from 'utils/fnTranslate';
import { FetchParamRent, PinType } from 'types/ApiTypes';
import { useGlobalNetworkError, useGlobalNoInternet } from 'hooks/withNetworkCheck/withNetworkCheck';
import { PinFlowType, usePin } from 'providers/usePin/PinContext';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { UpsellOptionSelector } from 'components/UpsellOptionSelector/UpsellOptionSelector';
import { getOfferTypeByTag, getPurchaseErrorCode, getPurchaseErrorTranslationKeys } from 'utils/fnEntitlement';
import { ServiceOptionCover } from 'types/Cover';
import { isEpisode, isRentable } from 'utils/fnTypeGuards';
import { useSubscription } from 'providers/useSubscription/SubscriptionContext';
import { SubscriptionConfirmation } from 'components/SubscriptionConfirmation/SubscriptionConfirmation';
import { Rentable } from 'types/CommonTypes';
import { useTransactionSuccessNotification } from 'components/Notification/Notification';
import { buildRentalPeriod } from 'utils/fnDate';
import { useVODPurchases } from 'providers/useVODPurchases/useVODPurchases';

export enum PurchaseFlowStep {
    SELECT_OFFER = 'select_offer',
    CONFIRM_PURCHASE_OFFER = 'confirm_purchase_offer',
    CONFIRM_SUBSCRIPTION_OFFER = 'confirm_subscription_offer',
    ENTER_PIN = 'enter_pin',
    ERROR = 'renting_subscribe_error',
}

const usePurchasingService = () => {
    const [asset, setAsset] = useState<Episode | Movie | MovieDetails>(null);
    const [step, setStep] = useState<PurchaseFlowStep>();
    const [purchaseType, setPurchaseType] = useState<'rent' | 'buy'>(null);
    const [vodTermId, setVodTermId] = useState(null);
    const [playPath, setPlayPath] = useState<string>(null);
    const [purchaseErrorCode, setPurchaseErrorCode] = useState<string>(null);

    const [selectedOffer, setSelectedOffer] = useState<Rentable | SubscribeOption>(null);
    const [selectedRentOffer, setSelectedPurchaseOffer] = useState<RentData>(null);

    const navigate = useNavigate();
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();
    const { openPinDialog, closePinDialog, validatedPurchasePin } = usePin();

    const dispatch = useDispatch();
    const confirmTransactionSuccess = useTransactionSuccessNotification();

    const {
        error,
        response,
        responseCode,
        loading,
        fetcher: getPurchaseVod,
        reset,
    } = useDataFetcher<any, FetchParamRent>(() => GSApi.purchaseVod(vodTermId, validatedPurchasePin), onNoInternet, onNetworkError);

    const {
        subscriptionOffersLoading,
        subscriptionOffers,
        subscriptionOfferError,
        resetSubscriptionOffers,

        orderResponse,
        orderError,
        isOrdering,
        onSubscribeSuccessful,
    } = useSubscription();

    const initPurchaseFlow = (playerUrl: string, assetToPurchase: Episode | Movie | MovieDetails) => {
        setAsset(assetToPurchase);
        setPlayPath(playerUrl);
        const tvodTerm = assetToPurchase.vod_terms?.find((term) => term.type === 'TVOD');
        if (tvodTerm) {
            setVodTermId(tvodTerm.id);
        }

        setPurchaseType('rent');
        setStep(PurchaseFlowStep.CONFIRM_PURCHASE_OFFER);
    };

    const goToConfirmation = (flowStep: PurchaseFlowStep) => {
        if (selectedOffer && isRentable(selectedOffer)) {
            const { rentOffers = [], buyOffers = [] } = selectedOffer;

            if (rentOffers.length && !buyOffers.length) {
                setPurchaseType('rent');
            } else if (buyOffers.length && !rentOffers.length) {
                setPurchaseType('buy');
            }
        }
        setStep(flowStep);
    };

    const goToEnterPin = () => {
        setStep(PurchaseFlowStep.ENTER_PIN);
    };

    const exitFlow = () => {
        setAsset(null);
        setSelectedOffer(null);
        setSelectedPurchaseOffer(null);
        setStep(null);
        setPurchaseType(null);
        setPurchaseErrorCode(null);
        reset();

        closePinDialog(false);
    };

    const onPinValidated = () => {
        // PIN validation handled by useEffect watching validatedPurchasePin
    };

    const onPinDialogClosed = () => {
        exitFlow();
    };

    const onPurchaseSuccessful = () => {
        dispatch(setPurchaseSynced());
        exitFlow();
        navigate(playPath);
        confirmTransactionSuccess(() => {});
    };

    const selectOffer = (offer: Rentable | SubscribeOption) => {
        setSelectedOffer(offer);
    };

    const goBackToSelectOffer = () => {
        setSelectedOffer(null);
        setStep(PurchaseFlowStep.SELECT_OFFER);
    };

    const selectPurchaseOffer = (offer: Offer) => {
        setSelectedPurchaseOffer({
            offerId: offer.catalog_id, // not sure about this
            productId: offer.id,
        });
        if (offer.id) {
            setVodTermId(offer.id);
        }
        setStep(PurchaseFlowStep.ENTER_PIN);
    };

    useEffect(() => {
        if (selectedOffer) {
            goToConfirmation(
                isRentable(selectedOffer) ? PurchaseFlowStep.CONFIRM_PURCHASE_OFFER : PurchaseFlowStep.CONFIRM_SUBSCRIPTION_OFFER
            );
        }
    }, [selectedOffer]);

    useEffect(() => {
        if (selectedRentOffer) {
            goToEnterPin();
        }
    }, [selectedRentOffer]);

    useEffect(() => {
        const status = parseInt(`${responseCode}`, 10);
        const isSuccessStatus = status >= 200 && status < 300;
        if (isSuccessStatus && asset && !error && !purchaseErrorCode) {
            onPurchaseSuccessful();
        }
    }, [responseCode, response, error, purchaseErrorCode]);

    useEffect(() => {
        if (orderResponse && asset) {
            onSubscribeSuccessful();
        }
    }, [orderResponse]);

    useEffect(() => {
        if (step && (error || orderError)) {
            setStep(PurchaseFlowStep.ERROR);
        }
    }, [error, orderError]);

    useEffect(() => {
        if (step && step === PurchaseFlowStep.SELECT_OFFER) {
            setSelectedOffer(null);
        }

        if (step && (step === PurchaseFlowStep.CONFIRM_PURCHASE_OFFER || step === PurchaseFlowStep.CONFIRM_SUBSCRIPTION_OFFER)) {
            closePinDialog(false);
        }

        if (step && step === PurchaseFlowStep.ENTER_PIN) {
            openPinDialog(PinFlowType.CHECK, PinType.PURCHASE, {
                onPinValidated,
                onDialogClosed: onPinDialogClosed,
                onBack: () => {
                    const prevStep =
                        selectedOffer && !isRentable(selectedOffer)
                            ? PurchaseFlowStep.CONFIRM_SUBSCRIPTION_OFFER
                            : PurchaseFlowStep.CONFIRM_PURCHASE_OFFER;
                    setStep(prevStep);
                },
                canGoBack: true,
            });
        }

        if (step === PurchaseFlowStep.ERROR) {
            closePinDialog(false);
        }
    }, [step]);

    const { refreshVODPurchases } = useVODPurchases();
    const purchaseCompletedRef = useRef(false);

    useEffect(() => {
        const processPurchase = async () => {
            if (validatedPurchasePin && selectedRentOffer && vodTermId && !purchaseCompletedRef.current) {
                const purchaseResult = await getPurchaseVod({
                    vodTermId,
                    purchasePin: validatedPurchasePin,
                });

                purchaseCompletedRef.current = true;

                const errorCode = getPurchaseErrorCode(purchaseResult);
                if (errorCode) {
                    setPurchaseErrorCode(errorCode);
                    setStep(PurchaseFlowStep.ERROR);
                    return;
                }

                await refreshVODPurchases(asset?.id);
            }
        };

        processPurchase();
    }, [validatedPurchasePin, selectedRentOffer, vodTermId]);

    useEffect(
        () => () => {
            purchaseCompletedRef.current = false;
        },
        [validatedPurchasePin, selectedRentOffer, vodTermId]
    );

    return {
        pendingFlow: asset != null,
        isBuyFlow: purchaseType === 'buy',
        purchaseError: error,
        purchaseErrorCode,
        purchasing: loading,
        ordering: isOrdering,
        orderError,
        step,
        asset,
        subscriptionOffersLoading,
        subscriptionOffers,
        subscriptionOfferError,
        resetSubscriptionOffers,
        initPurchaseFlow,
        cancel: () => {
            exitFlow();
        },
        selectPurchaseOffer,
        selectOffer,
        resetApi: () => {
            reset();
        },

        selectedOffer,
        goToEnterPin,
        goBackToSelectOffer,
    };
};

export const PurchaseContext = createContext<ReturnType<typeof usePurchasingService>>(null);

export const PurchaseProvider = ({ children }) => {
    const service = usePurchasingService();

    return <PurchaseContext.Provider value={service}>{children}</PurchaseContext.Provider>;
};

export const usePurchase = () => useContext(PurchaseContext);

export const usePurchaseFlowScreenProvider = () => {
    const {
        pendingFlow,
        asset,
        step,
        purchaseError,
        purchaseErrorCode,
        subscriptionOffers,
        subscriptionOffersLoading,
        subscriptionOfferError,
        selectedOffer,
        cancel,
        selectOffer,
        goToEnterPin,
        goBackToSelectOffer,
    } = usePurchase();

    const getSelectOfferScreen = () => {
        const vodTerms = asset?.vod_terms ?? [];
        const tvodTerms = vodTerms.filter((term) => term.type === 'TVOD');
        const hasAdditionalServices = asset?.additional_services_available === true;

        if (step && hasAdditionalServices && !subscriptionOfferError && (subscriptionOffersLoading || !subscriptionOffers)) {
            return <LoadingIndicator overlay={true} />;
        }

        const options: ServiceOptionCover[] = [];
        const { rentOffers, buyOffers } = asset;

        if (tvodTerms.length > 0) {
            const tvodTerm = tvodTerms[0];
            const watchPeriodHours = tvodTerm.watch_period ? Math.round(tvodTerm.watch_period / 3600) : 48;

            options.push({
                id: tvodTerm.id,
                image: asset?.image,
                duration: `${watchPeriodHours}h`,
                price: tvodTerm.price,
                currency: tvodTerm.currency,
                title: asset.title,
                optionTypeLabel: translate(isEpisode(asset) ? 'TYPE_EPISODE' : 'TYPE_MOVIE'),
                type: isEpisode(asset) ? 'EPISODE' : 'MOVIE',
                rawData: null,
                rentableData: asset,
                onClick: () => {
                    selectOffer({
                        ...asset,
                        rentOffers: asset.rentOffers?.length ? asset.rentOffers : [{ offerValue: tvodTerm.price } as any],
                        buyOffers: [],
                    });
                },
            });
        } else if (rentOffers?.length) {
            const minPrice = rentOffers.sort((a, b) => b.offerValue - a.offerValue)?.[0]?.offerValue;

            options.push({
                id: asset?.id,
                image: asset?.image,
                duration: `${buildRentalPeriod(asset?.rentOffers?.[0]?.rentalPeriodInMinutes ?? 0)}h`,
                price: minPrice,
                currency: rentOffers[0].currency,
                title: asset.title,
                optionTypeLabel: translate(isEpisode(asset) ? 'TYPE_EPISODE' : 'TYPE_MOVIE'),
                type: isEpisode(asset) ? 'EPISODE' : 'MOVIE',
                rawData: null,
                rentableData: asset,
                onClick: () => {
                    selectOffer({
                        ...asset,
                        buyOffers: [],
                    });
                },
            });
        }

        if (buyOffers?.length) {
            const minPrice = buyOffers.sort((a, b) => b.offerValue - a.offerValue)?.[0]?.offerValue;

            options.push({
                id: asset?.id,
                image: asset?.image,
                duration: null,
                price: minPrice,
                currency: buyOffers[0].currency,
                title: asset.title,
                optionTypeLabel: translate(isEpisode(asset) ? 'TYPE_EPISODE' : 'TYPE_MOVIE'),
                type: isEpisode(asset) ? 'EPISODE' : 'MOVIE',
                rawData: null,
                rentableData: asset,
                onClick: () => {
                    selectOffer({
                        ...asset,
                        rentOffers: [],
                    });
                },
            });
        }

        if (subscriptionOffers && hasAdditionalServices) {
            const services: Service[] = Array.isArray(subscriptionOffers) ? subscriptionOffers : [];

            services.forEach((offer) => {
                const price = offer.price.value;
                const productId = offer.id;

                options.push({
                    id: `${productId}`,
                    title: offer.name,
                    price,
                    currency: 'EUR',
                    optionTypeLabel: getOfferTypeByTag(offer.type),
                    type: 'SUBSCRIPTION',
                    image: offer.image_by_type?.poster || asset?.image,
                    duration: translate('SUBSCRIPTION_PERIOD_MONTH'),
                    rawData: {
                        name: offer.name,
                        tags: [offer.type],
                        price,
                        productId,
                        poster: offer.image_by_type?.poster || '',
                        description: offer.description,
                    },
                    onClick: () => {
                        selectOffer({
                            name: offer.name,
                            tags: [offer.type],
                            price,
                            productId,
                            poster: offer.image_by_type?.poster || '',
                            description: offer.description,
                            assetId: asset?.id,
                            packageId: `${offer.id}`,
                        });
                    },
                });
            });
        } else if (subscriptionOffers && !hasAdditionalServices && asset.subscribeOffer) {
            const services: Service[] = Array.isArray(subscriptionOffers) ? subscriptionOffers : [];

            services.forEach((offer) => {
                const price = offer.price.value;
                const productId = offer.id;

                options.push({
                    id: `${productId}`,
                    title: offer.name,
                    price,
                    currency: 'EUR',
                    optionTypeLabel: getOfferTypeByTag(offer.type),
                    type: 'SUBSCRIPTION',
                    image: offer.image_by_type?.poster,
                    duration: translate('SUBSCRIPTION_PERIOD_MONTH'),
                    rawData: {
                        name: offer.name,
                        tags: [offer.type],
                        price,
                        productId,
                        poster: offer.image_by_type?.poster || '',
                        description: offer.description,
                    },
                    onClick: () => {
                        selectOffer({
                            name: offer.name,
                            tags: [offer.type],
                            price,
                            productId,
                            poster: offer.image_by_type?.poster || '',
                            description: offer.description,
                            assetId: asset?.id,
                            packageId: asset?.subscribeOffer?.productCode,
                        });
                    },
                });
            });
        }

        return <UpsellOptionSelector onClose={cancel} options={options} onBack={() => cancel()} />;
    };

    const getConfirmSubscriptionOfferScreen = () => {
        const hasTvodTerms = asset?.vod_terms?.some((t) => t.type === 'TVOD');
        const hasAdditionalServices = asset?.additional_services_available === true;
        const hasMultipleOptions = hasTvodTerms && hasAdditionalServices;

        return (
            <SubscriptionConfirmation
                onClose={cancel}
                onBack={() => {
                    if (hasMultipleOptions) {
                        goBackToSelectOffer();
                    } else {
                        cancel();
                    }
                }}
                onConfirm={goToEnterPin}
                selectedOffer={selectedOffer as SubscribeOption}
                loading={/* ordering */ null}
            />
        );
    };

    const getGenericError = () => {
        const genericError = subscriptionOfferError;

        if (genericError) {
            const code = parseInt(genericError?.response?.status, 10);

            return (
                <AlertDialog
                    title={translate('ERR_GENERIC_TITLE')}
                    bodyText={translate('ERR_GENERIC_BODY')}
                    hintText={`${translate('ERR_CODE')} ${code}`}
                    buttons={[
                        {
                            text: translate('CLOSE_BUTTON'),
                            onClick: cancel,
                        },
                    ]}
                />
            );
        }

        return null;
    };

    const getErrorAlert = () => {
        if (purchaseErrorCode) {
            const { titleKey, bodyKey, isHtmlBody } = getPurchaseErrorTranslationKeys(purchaseErrorCode);
            const bodyText = isHtmlBody ? <span dangerouslySetInnerHTML={{ __html: translate(bodyKey) }} /> : translate(bodyKey);

            return (
                <AlertDialog
                    title={translate(titleKey)}
                    bodyText={bodyText}
                    buttons={[
                        {
                            text: translate('OK_BUTTON'),
                            onClick: cancel,
                        },
                    ]}
                />
            );
        }

        if (purchaseError) {
            const code = parseInt(purchaseError?.response?.status, 10);

            return (
                <AlertDialog
                    title={translate('ERR_RENTAL_GENERIC_TITLE')}
                    bodyText={translate('ERR_RENTAL_GENERIC_BODY')}
                    hintText={`${translate('ERR_CODE')} ${code}`}
                    buttons={[
                        {
                            text: translate('OK_BUTTON'),
                            onClick: cancel,
                        },
                    ]}
                />
            );
        }

        /* return null; */
        return getGenericError();
    };

    const getScreen = () => {
        if (!step) {
            return null;
        }

        if (pendingFlow && !subscriptionOfferError) {
            if (step === PurchaseFlowStep.SELECT_OFFER) {
                return getSelectOfferScreen();
            }

            if (step === PurchaseFlowStep.CONFIRM_PURCHASE_OFFER) {
                return <PurchaseOptionConfirmation />;
            }

            if (step === PurchaseFlowStep.CONFIRM_SUBSCRIPTION_OFFER) {
                return getConfirmSubscriptionOfferScreen();
            }

            if (step === PurchaseFlowStep.ENTER_PIN) {
                return selectedOffer && !isRentable(selectedOffer) ? getConfirmSubscriptionOfferScreen() : <PurchaseOptionConfirmation />;
            }
        }

        return getErrorAlert();
    };

    return getScreen;
};
